import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, CreditCard, ShoppingCart, HelpCircle, Phone, MapPin, ArrowRight, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
const steps = [{
  icon: FileText,
  title: 'Check Eligibility',
  description: 'WIC is for pregnant women, new mothers, and children under 5. EBT/SNAP is based on household income.'
}, {
  icon: CreditCard,
  title: 'Get Your Card',
  description: "Apply through your local WIC office or SNAP office. You'll receive a benefits card."
}, {
  icon: ShoppingCart,
  title: 'Shop With Us',
  description: 'Browse our WIC-approved and EBT-eligible products. Look for the badges on product cards.'
}, {
  icon: CheckCircle2,
  title: 'Easy Checkout',
  description: 'Use your benefits card at checkout. Our staff is trained to help with any questions.'
}];
const faqs = [{
  question: 'What products can I buy with WIC?',
  answer: 'WIC covers specific nutritious foods including milk, eggs, cheese, cereals, fruits, vegetables, and more. Look for the green WIC badge on our products.'
}, {
  question: 'Can I use EBT for all products?',
  answer: 'EBT/SNAP can be used for most food items including our African and Caribbean groceries. Non-food items and prepared hot foods are not covered.'
}, {
  question: 'Do you accept WIC and EBT for delivery?',
  answer: 'Yes! We accept both WIC and EBT for delivery orders through our partner platforms. Contact us for more details.'
}, {
  question: 'How do I know if a product is WIC-approved?',
  answer: 'All WIC-approved products on our site display a green "WIC" badge. You can also filter products to show only WIC-approved items.'
}];
export function WICEBTGuidePage() {
  return <div className="min-h-screen bg-[#FAF8F5]" data-id="element-448">
      <TopNav data-id="element-449" />

      <main className="pb-24 md:pb-8" data-id="element-450">
        {/* Hero */}
        <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white py-16 md:py-24" data-id="element-451">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-452">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="max-w-3xl" data-id="element-453">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.1
            }} className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6" data-id="element-454">
                <CheckCircle2 className="w-5 h-5" data-id="element-455" />
                <span className="font-semibold" data-id="element-456">
                  Authorized WIC & EBT Retailer
                </span>
              </motion.div>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-457">
                Shop with Your WIC & EBT Benefits
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-lg md:text-xl text-white/90 mb-8" data-id="element-458">
                Diamond International Mart proudly accepts WIC and EBT/SNAP
                benefits. Find nutritious, authentic African and Caribbean
                groceries for your family.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} className="flex flex-col sm:flex-row gap-4" data-id="element-459">
                <Link to="/search" className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 px-6 py-3 rounded-xl font-bold hover:bg-emerald-50 transition-colors" data-id="element-460">
                  Shop WIC Products
                  <ArrowRight className="w-5 h-5" data-id="element-461" />
                </Link>
                <a href="tel:2406079834" className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-bold border border-white/30 hover:bg-white/20 transition-colors" data-id="element-462">
                  <Phone className="w-5 h-5" data-id="element-463" />
                  Call for Help
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 md:py-20" data-id="element-464">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-465">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-466">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-467">
                How It Works
              </h2>
              <p className="text-gray-600 max-w-xl mx-auto" data-id="element-468">
                Using your benefits at Diamond International Mart is easy
              </p>
            </motion.div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-469">
              {steps.map((step, index) => <motion.div key={index} variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 relative" data-id="element-470">
                  <div className="absolute -top-3 -left-3 w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-sm" data-id="element-471">
                    {index + 1}
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4" data-id="element-472">
                    <step.icon className="w-6 h-6 text-emerald-600" data-id="element-473" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2" data-id="element-474">{step.title}</h3>
                  <p className="text-sm text-gray-600" data-id="element-475">{step.description}</p>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Benefits Comparison */}
        <section className="py-16 md:py-20 bg-white" data-id="element-476">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-477">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-478">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-479">
                WIC vs EBT/SNAP
              </h2>
              <p className="text-gray-600 max-w-xl mx-auto" data-id="element-480">
                Understanding the difference between these programs
              </p>
            </motion.div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="grid md:grid-cols-2 gap-8" data-id="element-481">
              <motion.div variants={itemVariants} className="bg-emerald-50 rounded-2xl p-6 md:p-8 border border-emerald-100" data-id="element-482">
                <div className="flex items-center gap-3 mb-6" data-id="element-483">
                  <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center" data-id="element-484">
                    <span className="text-white font-bold" data-id="element-485">WIC</span>
                  </div>
                  <div data-id="element-486">
                    <h3 className="font-bold text-gray-900" data-id="element-487">
                      Women, Infants & Children
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-488">
                      Supplemental Nutrition
                    </p>
                  </div>
                </div>
                <ul className="space-y-3" data-id="element-489">
                  {['For pregnant & postpartum women', 'Infants and children under 5', 'Specific approved food items', 'Nutrition education included', 'Income-based eligibility'].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-490">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-491" />
                      {item}
                    </li>)}
                </ul>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-blue-50 rounded-2xl p-6 md:p-8 border border-blue-100" data-id="element-492">
                <div className="flex items-center gap-3 mb-6" data-id="element-493">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center" data-id="element-494">
                    <span className="text-white font-bold" data-id="element-495">EBT</span>
                  </div>
                  <div data-id="element-496">
                    <h3 className="font-bold text-gray-900" data-id="element-497">
                      Electronic Benefits Transfer
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-498">SNAP Benefits</p>
                  </div>
                </div>
                <ul className="space-y-3" data-id="element-499">
                  {['For low-income households', 'All household members eligible', 'Most food items covered', 'Monthly benefit amount', 'Works like a debit card'].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-500">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-501" />
                      {item}
                    </li>)}
                </ul>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 md:py-20" data-id="element-502">
          <div className="max-w-3xl mx-auto px-4 md:px-6" data-id="element-503">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-504">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-505">
                Frequently Asked Questions
              </h2>
            </motion.div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="space-y-4" data-id="element-506">
              {faqs.map((faq, index) => <motion.div key={index} variants={itemVariants} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-507">
                  <div className="flex items-start gap-3" data-id="element-508">
                    <HelpCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-509" />
                    <div data-id="element-510">
                      <h3 className="font-bold text-gray-900 mb-2" data-id="element-511">
                        {faq.question}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-512">{faq.answer}</p>
                    </div>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 md:py-20 bg-[#1E3A5F]" data-id="element-513">
          <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-514">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center" data-id="element-515">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-516">
                Need Help with Your Benefits?
              </h2>
              <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto" data-id="element-517">
                Our friendly staff is here to help you shop with WIC and EBT.
                Visit us or give us a call!
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-518">
                <a href="tel:2406079834" className="inline-flex items-center gap-2 bg-[#F4A261] hover:bg-[#e8955a] text-[#1E3A5F] px-6 py-3 rounded-xl font-bold transition-colors" data-id="element-519">
                  <Phone className="w-5 h-5" data-id="element-520" />
                  (240) 607-9834
                </a>
                <div className="flex items-center gap-2 text-white/80" data-id="element-521">
                  <MapPin className="w-5 h-5" data-id="element-522" />
                  <span data-id="element-523">3651 Leonardtown Rd, Waldorf, MD 20601</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <BottomNav data-id="element-524" />
    </div>;
}